import type { ContentStatus, Row, SectionDef, ValidationResult } from '../types'
import { normalizeText, parseList, parseMap } from './csv'
import { validId } from './id'

function similarity(a: string, b: string): number {
  const one = new Set(normalizeText(a).split(' ').filter(Boolean))
  const two = new Set(normalizeText(b).split(' ').filter(Boolean))
  if (!one.size || !two.size) return 0
  const overlap = [...one].filter((word) => two.has(word)).length
  return overlap / Math.max(one.size, two.size)
}


function topicTokens(value: string): Set<string> {
  const stopwords = new Set(['de', 'del', 'la', 'las', 'el', 'los', 'y', 'en', 'un', 'una'])
  const synonyms: Record<string, string> = {
    femenina: 'mujer', femenino: 'mujer', mujeres: 'mujer', mujer: 'mujer',
    democratico: 'democracia', democratica: 'democracia', democratizacion: 'democracia',
    electoral: 'eleccion', elecciones: 'eleccion', sufragio: 'voto', votacion: 'voto',
  }
  return new Set(normalizeText(value).split(' ').filter(Boolean).filter((word) => !stopwords.has(word)).map((word) => synonyms[word] || word))
}

function decodeCorrect(value: string): string {
  try {
    const parsed = JSON.parse(value || '""')
    return typeof parsed === 'string' ? parsed : value
  } catch {
    return value
  }
}

export function syncVisualFields(section: SectionDef, source: Row): Row {
  const row = { ...source }
  const fixedTypes: Record<string, string> = {
    'multiple-choice': 'multiple_choice',
    'true-false': 'true_false',
    ordering: 'ordering',
    matching: 'matching',
    'sources-questions': 'source',
    development: 'development',
  }
  if (fixedTypes[section.id]) row.type = row.type || fixedTypes[section.id]
  row.vocabulary_level = row.vocabulary_level || 'Estándar'
  row.support_intensity = row.support_intensity || 'Sin apoyo adicional'
  row.status = row.status || (['false', '0', 'no'].includes(normalizeText(row.active || 'true')) ? 'Archivado' : 'Activo')
  row.version = row.version || 'V1'
  row.stimulus_type = row.stimulus_type || (row.source_id ? 'Fuente histórica e imagen' : 'Texto e imagen')
  row.feedback_correct = row.feedback_correct || row.feedback || ''
  row.feedback_incorrect = row.feedback_incorrect || row.feedback || ''
  row.hint_standard = row.hint_standard || row.hint || ''

  if (['multiple-choice', 'sources-questions'].includes(section.id)) {
    row.visual_options = row.visual_options || row.options || '[]'
    row.visual_correct_answer = row.visual_correct_answer || decodeCorrect(row.correct_answer || '')
    const options = parseList(row.visual_options)
    row.options = JSON.stringify(options)
    row.correct_answer = JSON.stringify(row.visual_correct_answer)
  }
  if (section.id === 'true-false') {
    row.visual_true_false = row.visual_true_false || decodeCorrect(row.correct_answer || '')
    row.justification = row.justification || row.expected_answer || ''
    row.options = JSON.stringify(['Verdadero', 'Falso'])
    row.correct_answer = JSON.stringify(row.visual_true_false)
    row.expected_answer = row.justification
  }
  if (section.id === 'ordering') {
    row.visual_ordering = row.visual_ordering || row.options || '[]'
    const options = parseList(row.visual_ordering)
    row.options = JSON.stringify(options)
    row.correct_answer = JSON.stringify(options)
  }
  if (section.id === 'matching') {
    row.visual_matching = row.visual_matching || row.correct_answer || '{}'
    const relations = parseMap(row.visual_matching)
    row.correct_answer = JSON.stringify(relations)
    row.options = JSON.stringify(Object.values(relations))
  }
  row.feedback = row.feedback_correct || row.feedback || ''
  row.hint = row.hint_standard || row.hint || ''
  return row
}

export function hydrateVisualFields(section: SectionDef, source: Row): Row {
  const row = { ...source }
  if (['multiple-choice', 'sources-questions'].includes(section.id)) {
    row.visual_options = row.options || '[]'
    row.visual_correct_answer = decodeCorrect(row.correct_answer || '')
  }
  if (section.id === 'true-false') {
    row.visual_true_false = decodeCorrect(row.correct_answer || '')
    row.justification = row.expected_answer || ''
  }
  if (section.id === 'ordering') row.visual_ordering = row.options || '[]'
  if (section.id === 'matching') row.visual_matching = row.correct_answer || '{}'
  row.feedback_correct = row.feedback_correct || row.feedback || ''
  row.hint_standard = row.hint_standard || row.hint || ''
  return row
}

export function validateRow(section: SectionDef, input: Row, rows: Row[], originalKey = ''): ValidationResult {
  const row = syncVisualFields(section, input)
  const errors: string[] = []
  const warnings: string[] = []
  const recommendations: string[] = []

  for (const field of section.fields) {
    if (field.advanced) continue
    if (field.required && !String(row[field.key] ?? '').trim()) errors.push(`Falta completar “${field.label}”.`)
  }

  const key = String(row[section.keyField] || '').trim()
  if (!key) errors.push('Falta el identificador.')
  else if (section.id !== 'question-files' && !validId(key)) errors.push('El identificador solo puede contener letras mayúsculas, números y guiones.')
  if (key && rows.some((candidate) => candidate[section.keyField] === key && key !== originalKey)) errors.push(`El identificador “${key}” ya existe.`)

  if (section.id === 'students') {
    if (!/^[0-9]{4}[A-Z]?$/.test(row.code || '')) errors.push('El código debe contener cuatro dígitos y, opcionalmente, una letra final.')
    if (!String(row.course || '').startsWith('6°')) errors.push('Este Gestor trabaja exclusivamente con cursos de 6° Básico.')
  }

  if (section.id === 'topics') {
    const candidate = normalizeText(`${row.topic || ''} ${row.subtopic || ''}`)
    const duplicates = rows.filter((item) => item[section.keyField] !== originalKey).filter((item) => {
      const existing = normalizeText(`${item.topic || ''} ${item.subtopic || ''}`)
      if (!candidate || !existing) return false
      const words = topicTokens(candidate)
      const existingWords = topicTokens(existing)
      const overlap = [...words].filter((word) => existingWords.has(word)).length
      return words.size > 0 && existingWords.size > 0 && overlap / Math.max(words.size, existingWords.size) >= 0.65
    })
    if (duplicates.length) warnings.push(`Este tema se parece a: ${duplicates.slice(0, 3).map((item) => item.topic || item.subtopic).join(', ')}. Decide si usar el existente o crear uno nuevo.`)
  }

  if (section.id === 'sessions') {
    const count = Number(row.question_count)
    if (!Number.isFinite(count) || count < 1 || count > 30) errors.push('La cantidad de preguntas debe estar entre 1 y 30.')
    if (row.grade && row.grade !== '6° básico') errors.push('La sesión debe corresponder a 6° Básico.')
  }

  const isMultiple = ['multiple-choice', 'sources-questions'].includes(section.id)
  if (isMultiple) {
    const options = parseList(row.options || '')
    if (options.length !== 4) errors.push('Deben existir exactamente cuatro alternativas.')
    if (options.some((option) => !option.trim())) errors.push('Todas las alternativas deben contener texto.')
    const correct = decodeCorrect(row.correct_answer || '')
    if (!correct) errors.push('Marca la respuesta correcta.')
    else if (!options.includes(correct)) errors.push('La respuesta correcta no coincide exactamente con ninguna alternativa.')
    const normalized = options.map(normalizeText)
    if (new Set(normalized).size !== normalized.length) errors.push('Hay alternativas repetidas o prácticamente iguales.')
    if (options.some((option, index) => options.some((other, otherIndex) => index !== otherIndex && similarity(option, other) >= 0.8))) warnings.push('Dos alternativas son muy parecidas y podrían generar ambigüedad.')
    const promptWords = new Set(normalizeText(row.prompt || '').split(' '))
    const correctWords = normalizeText(correct).split(' ').filter((word) => word.length > 4)
    const overlap = correctWords.filter((word) => promptWords.has(word)).length
    if (correctWords.length && overlap / correctWords.length > 0.65) warnings.push('La respuesta correcta repite demasiadas palabras del enunciado y podría entregar una pista.')
    if (options.some((option) => /\b(siempre|nunca|todos|ninguno)\b/i.test(option))) warnings.push('Revisa palabras absolutas como “siempre”, “nunca”, “todos” o “ninguno”.')
    if (options.some((option) => /^(no sé|ninguna de las anteriores|todas las anteriores)$/i.test(option.trim()))) warnings.push('Hay un distractor muy genérico. Conviene reemplazarlo por una idea histórica plausible.')
    recommendations.push('Procura que cada distractor represente un error frecuente o una confusión histórica posible.')
  }

  if (section.id === 'true-false') {
    const prompt = row.prompt || ''
    if (/\by\b.+\by\b/i.test(prompt) || /;/.test(prompt)) warnings.push('El enunciado podría contener más de una idea. Verdadero o falso debe evaluar una sola afirmación.')
    if (!row.justification?.trim() && !row.expected_answer?.trim()) warnings.push('Agrega una justificación para la respuesta.')
  }

  if (section.id === 'matching' && Object.keys(parseMap(row.correct_answer || '')).length < 2) errors.push('Agrega al menos dos relaciones correctas.')
  if (section.id === 'ordering' && parseList(row.options || '').length < 3) errors.push('Agrega al menos tres elementos para ordenar.')
  if (section.id === 'development') {
    if (parseList(row.keywords || '').length < 2) errors.push('Agrega al menos dos palabras clave.')
    if (parseList(row.achievement_criteria || '').length < 2) errors.push('Agrega al menos dos criterios de logro.')
  }

  if (row.prompt && row.prompt.length > 500) warnings.push('El enunciado es extenso. Considera dividirlo o preparar una versión adaptada.')
  if (row.prompt && !/[?¿.!:]$/.test(row.prompt.trim())) warnings.push('Revisa la puntuación final del enunciado.')
  if (row.keywords && parseList(row.keywords).length > 10) warnings.push('Hay demasiadas palabras clave. Prioriza las esenciales.')
  if (row.image_path && row.image_status === 'Pendiente de subir a GitHub') errors.push('La imagen asociada todavía está pendiente de subir a GitHub. Puedes guardar un borrador, pero no publicarla.')
  if (['Activo', 'Listo para usar'].includes(row.status || '') && errors.length) recommendations.push('Mantén el contenido como borrador hasta corregir todos los errores técnicos.')
  if (warnings.length && row.status === 'Activo') recommendations.push('Los problemas pedagógicos leves deben dejar el contenido “En revisión” antes de activarlo.')

  const statuses: ContentStatus[] = ['Borrador', 'En revisión', 'Listo para usar', 'Activo', 'Archivado']
  const currentStatus = statuses.includes(row.status as ContentStatus) ? row.status as ContentStatus : 'Listo para usar'
  const statusSuggestion: ContentStatus = errors.length ? 'Borrador' : warnings.length ? 'En revisión' : currentStatus
  return { errors, warnings, recommendations, statusSuggestion }
}

export function probableDuplicate(value: string, values: string[]): string[] {
  return values.filter((candidate) => candidate !== value && similarity(value, candidate) >= 0.65).slice(0, 5)
}
